import * as yup from "yup";

const NYSC_STATE_CODE_PATTERN = /^[A-Z]{2}\/\d{2}[A-Z]\/\d+$/i;

/* ---------------------------
   Core validation messages
---------------------------- */
export const emailNotLongEnough = "Email must be at least 3 characters long";
export const passwordNotLongEnough = "Password must be at least 8 characters long";
export const invalidEmail = "Please enter a valid email address";
export const invalidPhone = "Please enter a valid phone number";
export const invalidCode = "Please enter a valid redeem code";
export const invalidMobile = "Please enter a valid mobile number for your country";

/* ---------------------------
   Auth and username messages
---------------------------- */
const invalidLogin = "Your email or password is incorrect, please try again";
const invalidUsername = "Username must be at least 3 characters long";
const invalidUsername2 = "Username cannot be longer than 20 characters";


/* ---------------------------
   Password validation
---------------------------- */
export const registerPasswordValidation = yup
  .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(255)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]+$/, // included 2 hash tag to password validation
      "Password must contain at least an uppercase, lowercase letter, a number & a special character"
    )
;

/* ---------------------------
   User registration schema
---------------------------- */
export const validUserSchema = yup.object().shape({
  email: yup
    .string()
    .min(3, emailNotLongEnough)
    .max(255)
    .email()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      invalidEmail
    )
    .required("Email is required"),
  phone: yup
    .string()
    .min(10, invalidPhone)
    .max(15, invalidPhone)
    .matches(/^\+?\d{10,15}$/, "Enter a valid phone number (digits only, 10-15 characters)")
    .required("Phone number is required"),
  password: registerPasswordValidation,
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("password"), null as any], "Passwords do not match"),
  nda: yup
    .boolean()
    .required("NDA is required")
    .oneOf([true],"Accept NDA to continue"),
});

/* ---------------------------
   Admin registration schema
---------------------------- */
export const validAdminSchema = yup.object().shape({
  firstname: yup
    .string()
    .min(3, "First name must be at least 3 characters long")
    .max(100, "First name cannot be longer than 100 characters")
    .required("First name is required"),
  lastname: yup
    .string()
    .min(3, "Last name must be at least 3 characters long")
    .max(100, "Last name cannot be longer than 100 characters")
    .required("Last name is required"),
  companyName: yup
    .string()
    .min(3, "Company name must be at least 3 characters long")
    .max(100, "Company name cannot be longer than 100 characters")
    // .required("Company name is required"),
    .notRequired(),
  email: yup
    .string()
    .min(3, emailNotLongEnough)
    .max(255)
    .email(invalidEmail)
    .required("Email is required"),
  phone: yup
    .string()
    .min(3, invalidPhone)
    .max(20, invalidPhone)
    .required("Phone number is required"),
  password: registerPasswordValidation,
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password"), null as any], "Passwords do not match"),
  nda: yup
    .boolean()
    .required("You need to agree to the NDA before continuing"),
});

/* ---------------------------
   Login schema
---------------------------- */
export const loginSchema = yup.object().shape({
  email: yup
    .string()
    .min(3, invalidLogin)
    .max(255, invalidLogin)
    .email(invalidLogin)
    .required("Email is required"),
  password: yup
    .string()
    .min(8, invalidLogin)
    .max(255, invalidLogin)
    .required("Password is required"),
});

/* ---------------------------
   Finish signup schema
---------------------------- */
export const finishSignupSchema = yup.object().shape({
  firstname: yup
    .string()
    .min(3, "First name must be at least 3 characters long")
    .max(100, "First name cannot be longer than 100 characters")
    .required("First name is required"),
  lastname: yup
    .string()
    .min(3, "Last name must be at least 3 characters long")
    .max(100, "Last name cannot be longer than 100 characters")
    .required("Last name is required"),
  username: yup
  .string()
  .matches(
    /^[a-z]/,
    "Username must start with a lowercase letter"
  )
  .matches(
    /^[a-z0-9._]+$/,
    "Username can only contain lowercase letters, numbers, underscores, and dots"
  )
  .required("Username is required")
  .min(3, invalidUsername)
  .max(20, invalidUsername2),
  locationId: yup
    .string()
    .uuid()
    .required("Location ID is required"),
  pin: yup
    .string()
    .typeError("PIN must only contain numbers")
    .required("Please enter your 4-digit PIN")
    .test("len", "PIN must be exactly 4 digits", val => {
      if (typeof val !== "string") return false;
      return val.length === 4;
    }),
});

/* ---------------------------
   Finish signup schema
---------------------------- */
export const personalDetailSchema = yup.object().shape({
  username: yup
  .string()
  .matches(
    /^[a-z]/,
    "Username must start with a lowercase letter"
  )
  .matches(
    /^[a-z0-9._]+$/,
    "Username can only contain lowercase letters, numbers, underscores, and dots"
  )
  .min(3, invalidUsername)
  .max(20, invalidUsername2),
  dob: yup
    .string()
    .required("DOB is required"),
  address: yup
    .string()
    .required("Address is required"),
  gender: yup
    .string()
    .required("Gender is required"),
});

/* ---------------------------
   Wallet KYC schema
---------------------------- */
export const NINKYCSchema = yup.object().shape({
  nin: yup
    .number()
    .min(11)
    .required("NIN is required"),
  firstname: yup
    .string()
    .required("First name is required"),
  lastname: yup
    .string()
    .required("Last name is required"),
});

/* ---------------------------
   Finish signup schema
---------------------------- */
export const kycSchema = yup.object().shape({
  nin: yup
    .number()
    .min(11)
    .required("NIN is required"),
  dob: yup
    .string()
    .required("DOB is required"),
  bvn: yup
    .number()
    .min(11)
    .required("BVN is required"),
});

/* ---------------------------
   Connect ID schema
---------------------------- */
export const ConnectIdSchema = yup.object().shape({
  communityId: yup
    .string()
    .uuid()
    .required("Community ID is required"),
  id: yup.string().when("requiresCommunityEntity", {
    is: true,
    then: (schema) =>
      schema.when("isStudentCommunity", {
        is: true,
        then: (nestedSchema) => nestedSchema.required("University is required"),
        otherwise: (nestedSchema) =>
          nestedSchema.required("Sub community is required")
      }),
    otherwise: (schema) => schema.notRequired()
  }),
  idNumber: yup
    .string()
    .when("isCbmCommunity", {
      is: true,
      then: (schema) =>
        schema
          .test(
            "cbm-member-id-format",
            "Member ID is required",
            (value) => Boolean(value?.trim())
          )
          .required("Member ID is required"),
      otherwise: (schema) =>
        schema.when(["isStudentCommunity", "id"], {
          is: (isStudentCommunity: boolean, id?: string) =>
            !isStudentCommunity || Boolean(id),
          then: (nestedSchema) =>
            nestedSchema
              .min(7, "ID number must be at least 7 characters long")
              .max(15, "ID number cannot be longer than 15 characters")
              .test(
                "nysc-state-code-format",
                "Enter valid state code format",
                function (value) {
                  if (!this.parent.isNyscCommunity || !value?.trim()) {
                    return true;
                  }

                  return NYSC_STATE_CODE_PATTERN.test(value.trim());
                }
              )
              .required("ID number is required"),
          otherwise: (nestedSchema) => nestedSchema.notRequired()
        })
    }),
  isStudentCommunity: yup.boolean().default(false),
  isNyscCommunity: yup.boolean().default(false),
  isCbmCommunity: yup.boolean().default(false),
  requiresCommunityEntity: yup.boolean().default(false)
});

/* ---------------------------
   Reset password schema
---------------------------- */
export const resetPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .min(3, emailNotLongEnough)
    .email(invalidEmail)
    .required("Email is required"),
});

/* ---------------------------
   Redeem schema
---------------------------- */
export const redeemSchema = yup.object().shape({
  code: yup
    .string()
    .min(13, invalidCode)
    .required("Redeem code is required"),
});

/* ---------------------------
   Preference schema
---------------------------- */
export const preferenceSchema = yup.object().shape({
  genreId: yup
    .array()
    // .uuid()
    .required("Genre ID is required"),
  gameId: yup
    .array()
    // .uuid()
    .required("Game ID is required"),
  platformId: yup
    .array()
    // .uuid()
    .required("Platform ID is required"),
  mobile: yup
    .string()
    .min(9, invalidMobile)
    .max(11, invalidMobile)
    .required("Mobile number is required"),
});

/* ---------------------------
   PIN & OTP schemas
---------------------------- */
export const pinOTPSchema = yup.object().shape({
  pin: yup
    .number()
    .required("PIN is required"),
});

export const validateOTPSchema = yup.object().shape({
  verificationCode: yup
    .number()
    .required("Please enter your OTP"),
});

/* ---------------------------
   Security questions schema
---------------------------- */
export const securityQuestionSchema = yup.object().shape({
  question1: yup.string().required(),
  answer1: yup.string().required(),
  question2: yup.string().required(),
  answer2: yup.string().required(),
});

/* ---------------------------
   Change password schema
---------------------------- */
export const changePasswordSchema = yup.object().shape({
  newPassword: registerPasswordValidation,
  confirmNewPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword"), null as any], "New passwords do not match"),
});

export const profileChangePasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required("Current password is required"),
  newPassword: yup
    .string()
    .required("New password is required")
    .min(8, "New password must be at least 8 characters")
    .max(255)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]+$/, // included 2 hash tag to password validation
      "New password must contain at least an uppercase, lowercase letter, a number & a special character"
    ),
  confirmNewPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword"), null as any], "New passwords do not match"),
});
